import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useProjects } from '../context/ProjectsContext';
import ProjectGrid from '../components/ProjectGrid';
import LoadingSpinner from '../components/LoadingSpinner';

const Projects = () => {
  const { projects, loading, error } = useProjects();
  const [activeTab, setActiveTab] = useState('professional');

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-poppins text-2xl font-bold text-primary mb-4">
            Unable to load projects
          </h2>
          <p className="font-barlow text-tertiary">
            Please try again later.
          </p>
        </div>
      </div>
    );
  }

  // M.Arch Portfolio project data
  const marchProject = {
    id: 'march-portfolio',
    'Project Name': 'M.Arch Portfolio – Y2021',
    'Project Overview': "Discover a collection of architectural projects from my Master's degree at Florida International University, completed 2021, reflecting a strong foundation in design thinking and technical execution.",
    'Project Image 1': 'https://content.raheem.work/file/RB/College-Architectural-Projects/M.Arch-Portfolio-Cover-Photo.jpg',
    isExternal: true,
    externalUrl: 'https://link.raheem.work/m-arch-portfolio'
  };

  const displayProjects = activeTab === 'professional' ? projects : [marchProject];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen py-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-16"
        >
          <h1 className="font-poppins text-5xl font-bold text-primary mb-6">
            Portfolio
          </h1>
          <p className="font-barlow text-lg text-tertiary max-w-2xl mx-auto">
            {activeTab === 'march' 
              ? 'View my Masters Degree portfolio.' 
              : 'A comprehensive collection of architectural projects spanning retail, office, and commercial sectors, showcasing innovative design solutions and sustainable practices.'
            }
          </p>
          <div className="w-24 h-1 bg-accent-primary mx-auto mt-6"></div>
        </motion.div>

        {/* Tab Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex justify-center mb-12"
        >
          <div className="bg-gray-100 p-1 rounded-lg inline-flex">
            <button
              onClick={() => setActiveTab('professional')}
              className={`px-6 py-3 font-barlow font-medium rounded-md transition-all duration-300 ${
                activeTab === 'professional'
                  ? 'bg-accent-primary text-white shadow-md'
                  : 'text-accent-primary hover:bg-gray-200'
              }`}
            >
              Professional Work
            </button>
            <button
              onClick={() => setActiveTab('march')}
              className={`px-6 py-3 font-barlow font-medium rounded-md transition-all duration-300 ${
                activeTab === 'march'
                  ? 'bg-accent-primary text-white shadow-md'
                  : 'text-accent-primary hover:bg-gray-200'
              }`}
            >
              M.Arch Portfolio
            </button>
          </div>
        </motion.div>

        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {activeTab === 'march' ? (
            <div className="w-full">
              <div 
                onClick={() => window.open(marchProject.externalUrl, '_blank')}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group hover:transform hover:scale-105"
              >
                <div className="bg-quaternary overflow-hidden h-64">
                  <img
                    src={marchProject['Project Image 1']}
                    alt={marchProject['Project Name']}
                    className="w-full h-full object-cover object-bottom group-hover:opacity-90 transition-opacity duration-300"
                  />
                </div>
                
                <div className="p-8">
                  <h3 className="font-blanka text-2xl font-bold text-primary mb-4 group-hover:text-accent-primary transition-colors duration-300">
                    {marchProject['Project Name']}
                  </h3>
                  
                  <p className="font-barlow text-tertiary mb-6 text-lg leading-relaxed">
                    {marchProject['Project Overview']}
                  </p>
                  
                  <div className="inline-flex items-center text-accent-primary font-barlow font-medium group-hover:text-accent-secondary transition-colors duration-300">
                    View Portfolio →
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ProjectGrid projects={displayProjects} />
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Projects;